'use client';

import cx from 'classix';
import { setCookie } from 'cookies-next/client';
import Image from 'next/image';
import Link from 'next/link';
import {
  useLocale,
  useTranslations,
} from 'next-intl';
import {
  BsStarFill,
  BsStarHalf,
} from 'react-icons/bs';
import {
  FaFacebookF,
  FaInstagram,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

import useAppFlyerLink from '@/features/roomster-app/hooks/use-app-flyer-link';
import {
  CATEGORY_FOOTER_LINKS,
  DOWNLOAD_APP_LINKS,
  EXTERNAL_LINKS,
  ROUTER_LINKS,
  SOCIAL_MEDIA_LINKS,
} from '@/shared/constants/links-constants';
import { SECONDS } from '@/shared/constants/time-constants';

import styles from './home-page-footer-section.module.scss';

const fullStars = Array.from({ length: 4 }, (_, index) => index);

export default function HomePageFooterSection() {
  const footerTranslations = useTranslations('home.footer');
  const linksTranslations = useTranslations('global.links');
  const legalTranslations = useTranslations('global.legal');
  const locale = useLocale();
  const country = locale.split('-')[1].toLowerCase();

  const handleSupportAuth = () => {
    setCookie('authData', JSON.stringify({ token: 'customer-service' }), { maxAge: SECONDS.HOUR, path: '/' });
  };

  return (
    <section className={styles.homePageFooterWrapper}>
      <div className={styles.homePageFooterSection}>
        <div className={styles.homePageFooterColumnsWrapper}>
          <div className={styles.homePageFooterSection_desktop}>
            <div className={styles.homePageFooterSectionMenu_column}>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.press}
              >
                {footerTranslations('press')}
              </Link>

              {/* TODO: [auth-redirect] */}

              <Link
                prefetch={false}
                className={styles.footerLink}
                href={useAppFlyerLink('mobile', 'mobileMailbox', ROUTER_LINKS.phoneAuth)}
              >
                {footerTranslations('mailbox')}
              </Link>
              {/* TODO: [auth-redirect] */}
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={useAppFlyerLink('mobile', 'mobileContactUs', ROUTER_LINKS.phoneAuth)}
              >
                {footerTranslations('contact-us-header')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.sitemap('rooms-for-rent')}
                target="_blank"
              >
                {footerTranslations('sitemap')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.reviews}
              >
                {linksTranslations('reviews')}
              </Link>
              <Link
                className={styles.footerLink}
                prefetch={false}
                href={ROUTER_LINKS.blog}
                target="_blank"
              >
                {footerTranslations('blog')}
              </Link>
            </div>
            <div className={styles.homePageFooterSectionMenu_column}>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.downloadApp}
              >
                {footerTranslations('download-apps')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.findingRoom}
              >
                {footerTranslations('finding-a-room')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.rentingOutRoom}
              >
                {footerTranslations('renting-out-a-room')}
              </Link>
              {/* The four national category pages. Nothing on the site linked to
                  them before this — measured 2026-09-04, zero inbound internal
                  links and zero impressions in six days. The home page is the
                  strongest internal link source there is, so they belong here.
                  Ungated, matching the rest of this component, which has no
                  auth branch. */}
              {CATEGORY_FOOTER_LINKS.map(({ listingType, translationKey }) => (
                <Link
                  key={listingType}
                  prefetch={false}
                  className={styles.footerLink}
                  href={ROUTER_LINKS.listingTypeCategory(listingType)}
                >
                  {linksTranslations(translationKey)}
                </Link>
              ))}
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.roommateFinder}
              >
                {linksTranslations('roommate-finder')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.trustAndSafety}
              >
                {linksTranslations('trust-and-safety')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.communityStandards}
              >
                {linksTranslations('community-standards')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.safeRoommateMatching}
              >
                {linksTranslations('safe-roommate-matching')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.howRoomsterWorks}
              >
                {linksTranslations('how-roomster-works')}
              </Link>
            </div>
            <div className={styles.homePageFooterSectionMenu_column}>
              <Link
                // prefetch disabled, because this link protected by nginx
                // and when this link prefetch it also ask on current page for auth
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.partner}
              >
                {linksTranslations('partner')}
              </Link>
              {/* TODO: [auth-redirect] */}
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={useAppFlyerLink('mobile', 'mobileMegaphone', ROUTER_LINKS.phoneAuth)}
              >
                {footerTranslations('megaphone')}
              </Link>
              {/* TODO: [auth-redirect] */}
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={useAppFlyerLink('mobile', 'mobileBookmark', ROUTER_LINKS.phoneAuth)}
              >
                {footerTranslations('bookmark-title-s')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                onClick={handleSupportAuth}
                href={useAppFlyerLink('mobile', 'mobileSupport', ROUTER_LINKS.phoneAuth)}
              >
                {footerTranslations('support')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={useAppFlyerLink('mobile', 'mobileSettings', ROUTER_LINKS.phoneAuth)}
              >
                {footerTranslations('settings-title')}
              </Link>
              <a
                className={styles.footerLink}
                href={EXTERNAL_LINKS.affiliateProgram}
                target="_blank"
                rel="noreferrer"
              >
                {footerTranslations('affiliate-program')}
              </a>
            </div>
            <div className={styles.homePageFooterSectionMenu_column}>
              {/* TODO: [auth-redirect] */}
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={useAppFlyerLink('mobile', 'mobileHelpCenter', ROUTER_LINKS.phoneAuth)}
              >
                {footerTranslations('help-center')}
              </Link>
              <a
                className={styles.footerLink}
                href={EXTERNAL_LINKS.faq}
                target="_blank"
                rel="noreferrer"
              >
                {footerTranslations('faq')}
              </a>
              <a
                className={styles.footerLink}
                href={EXTERNAL_LINKS.fairHousing}
                target="_blank"
                rel="noreferrer"
              >
                {footerTranslations('fair')}
              </a>
            </div>
            <div className={styles.homePageFooterSectionMenu_column}>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.terms}
              >
                {footerTranslations('terms')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.privacy}
              >
                {footerTranslations('privacy')}
              </Link>
              <button
                className={styles.footerLink}
                onClick={(event) => {
                  event.preventDefault();

                  // @ts-expect-error - lib cookiesyes included as script
                  if (globalThis.revisitCkyConsent) {
                    // @ts-expect-error - lib cookiesyes included as script
                    globalThis.revisitCkyConsent();
                  }
                }}
              >
                {legalTranslations('do-not-share-personal-information')}
              </button>
              <div className={styles.mediaLinks}>
                <a
                  className={cx(styles.footerLink, styles.socialItem)}
                  href={SOCIAL_MEDIA_LINKS.facebook}
                  target="_blank"
                  aria-label="FacebookIcon"
                  rel="noreferrer"
                >
                  <FaFacebookF />
                </a>
                <a
                  className={cx(styles.footerLink, styles.socialItem)}
                  href={SOCIAL_MEDIA_LINKS.twitter}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="TwitterIcon"
                >
                  <FaXTwitter />
                </a>
                <a
                  className={cx(styles.footerLink, styles.socialItem)}
                  href={SOCIAL_MEDIA_LINKS.instagram}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="InstagramIcon"
                >
                  <FaInstagram />
                </a>
              </div>
            </div>
          </div>

          <div className={styles.homePageFooterSection_mobile}>
            <div className={styles.homePageFooterSectionMenu_column}>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.press}
              >
                {footerTranslations('press')}
              </Link>
              {/* TODO: [auth-redirect] */}
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={useAppFlyerLink('mobile', 'mobileContactUs', ROUTER_LINKS.phoneAuth)}
              >
                {footerTranslations('contact-us-header')}
              </Link>
              {/* TODO: [auth-redirect] */}
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={useAppFlyerLink('mobile', 'mobileBookmark', ROUTER_LINKS.phoneAuth)}
              >
                {footerTranslations('bookmark-title-s')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.sitemap('rooms-for-rent')}
                target="_blank"
              >
                {footerTranslations('sitemap')}
              </Link>
              {/* TODO: [auth-redirect] */}
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={useAppFlyerLink('mobile', 'mobileSettings', ROUTER_LINKS.phoneAuth)}
              >
                {footerTranslations('settings-title')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.downloadApp}
              >
                {footerTranslations('download-apps')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.findingRoom}
              >
                {footerTranslations('finding-a-room')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.rentingOutRoom}
              >
                {footerTranslations('renting-out-a-room')}
              </Link>
              {/* The four national category pages. Nothing on the site linked to
                  them before this — measured 2026-09-04, zero inbound internal
                  links and zero impressions in six days. The home page is the
                  strongest internal link source there is, so they belong here.
                  Ungated, matching the rest of this component, which has no
                  auth branch. */}
              {CATEGORY_FOOTER_LINKS.map(({ listingType, translationKey }) => (
                <Link
                  key={listingType}
                  prefetch={false}
                  className={styles.footerLink}
                  href={ROUTER_LINKS.listingTypeCategory(listingType)}
                >
                  {linksTranslations(translationKey)}
                </Link>
              ))}
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.roommateFinder}
              >
                {linksTranslations('roommate-finder')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.trustAndSafety}
              >
                {linksTranslations('trust-and-safety')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.communityStandards}
              >
                {linksTranslations('community-standards')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.safeRoommateMatching}
              >
                {linksTranslations('safe-roommate-matching')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.howRoomsterWorks}
              >
                {linksTranslations('how-roomster-works')}
              </Link>
              {/* TODO: [auth-redirect] */}
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={useAppFlyerLink('mobile', 'mobileMailbox', ROUTER_LINKS.phoneAuth)}
              >
                {footerTranslations('mailbox')}
              </Link>
              <a
                className={styles.footerLink}
                href={EXTERNAL_LINKS.affiliateProgram}
                target="_blank"
                rel="noreferrer"
              >
                {footerTranslations('affiliate-program')}
              </a>
            </div>
            <div className={styles.homePageFooterSectionMenu_column}>
              <div className={styles.footerLink}>
                <Image
                  src={`/images/country-flags-icons/${country}.svg`}
                  alt={country}
                  width={26}
                  height={18}
                />
              </div>
              <Link
                // prefetch disabled, because this link protected by nginx
                // and when this link prefetch it also ask on current page for auth
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.partner}
              >
                {linksTranslations('partner')}
              </Link>
              {/* TODO: [auth-redirect] */}
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={useAppFlyerLink('mobile', 'mobileMegaphone', ROUTER_LINKS.phoneAuth)}
              >
                {footerTranslations('megaphone')}
              </Link>
              {/* TODO: [auth-redirect] */}
              <Link
                prefetch={false}
                className={styles.footerLink}
                onClick={handleSupportAuth}
                href={useAppFlyerLink('mobile', 'mobileSupport', ROUTER_LINKS.phoneAuth)}
              >
                {footerTranslations('support')}
              </Link>
              <a
                className={styles.footerLink}
                href={EXTERNAL_LINKS.fairHousing}
                target="_blank"
                rel="noreferrer"
              >
                {footerTranslations('fair')}
              </a>
              {/* TODO: [auth-redirect] */}
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={useAppFlyerLink('mobile', 'mobileHelpCenter', ROUTER_LINKS.phoneAuth)}
              >
                {footerTranslations('help-center')}
              </Link>
              <a
                className={styles.footerLink}
                href={EXTERNAL_LINKS.faq}
                target="_blank"
                rel="noreferrer"
              >
                {footerTranslations('faq')}
              </a>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.reviews}
              >
                {linksTranslations('reviews')}
              </Link>
              <Link
                className={styles.footerLink}
                prefetch={false}
                href={ROUTER_LINKS.blog}
                target="_blank"
              >
                {footerTranslations('blog')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.terms}
              >
                {footerTranslations('terms')}
              </Link>
              <Link
                prefetch={false}
                className={styles.footerLink}
                href={ROUTER_LINKS.privacy}
              >
                {footerTranslations('privacy')}
              </Link>
              <button
                className={styles.footerLink}
                onClick={(event) => {
                  event.preventDefault();

                  // @ts-expect-error - lib cookiesyes included as script
                  if (globalThis.revisitCkyConsent) {
                    // @ts-expect-error - lib cookiesyes included as script

                    globalThis.revisitCkyConsent();
                  }
                }}
              >
                {legalTranslations('do-not-share-personal-information')}
              </button>
              <div className={styles.mediaLinks}>
                <a
                  className={cx(styles.footerLink, styles.socialItem)}
                  href={SOCIAL_MEDIA_LINKS.facebook}
                  target="_blank"
                  aria-label="FacebookIcon"
                  rel="noreferrer"
                >
                  <FaFacebookF />
                </a>
                <a
                  className={cx(styles.footerLink, styles.socialItem)}
                  href={SOCIAL_MEDIA_LINKS.twitter}
                  target="_blank"
                  aria-label="TwitterIcon"
                  rel="noreferrer"
                >
                  <FaXTwitter />
                </a>
                <a
                  className={cx(styles.footerLink, styles.socialItem)}
                  href={SOCIAL_MEDIA_LINKS.instagram}
                  target="_blank"
                  aria-label="InstagramIcon"
                  rel="noreferrer"
                >
                  <FaInstagram />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.homePageFooterApps}>
          <div className={styles.homePageFooterAppsRow}>
            <div className={styles.homePageFooterAppsRow_text}>
              {footerTranslations('download-free')}
            </div>
            <div className={styles.homePageFooterAppsRow_rating}>
              {fullStars.map((item) => (
                <div key={item} className={styles.startContainerItem}>
                  <BsStarFill />
                </div>
              ))}
              <div className={styles.startContainerItem}>
                <BsStarHalf />
              </div>
            </div>
          </div>

          <div className={styles.marketingList}>
            <a
              aria-label="appStore"
              className={styles.marketingList_link}
              href={DOWNLOAD_APP_LINKS.appStore}
              rel="noreferrer"
            >
              <Image
                src="/images/banner/tunes.webp"
                alt="full star"
                fill
              />
            </a>
            <a
              aria-label="googlePlay"
              className={styles.marketingList_link}
              href={DOWNLOAD_APP_LINKS.googlePlay}
              rel="noreferrer"
              target="_blank"
            >
              <Image
                src="/images/banner/google-play.webp"
                alt="full star"
                fill
              />
            </a>
          </div>
          <div className={styles.copyRight}>
            Roomster &copy;
            {' '}
            {new Date().getFullYear()}
          </div>
        </div>
      </div>
    </section>
  );
}
